import React from 'react';
import Icon from 'components/AppIcon';

const AccommodationInfo = ({ accommodation = null }) => {
  const defaultAccommodation = {
    name: "Deluxe Forest Villa",
    type: "Villa",
    maxGuests: 4,
    bedrooms: 2,
    bathrooms: 2,
    area: "850 sq ft",
    description: `Experience luxury in the heart of nature with our Deluxe Forest Villa. This spacious accommodation offers panoramic views of the lush Dandeli forest, providing the perfect blend of comfort and wilderness adventure.\n\nThe villa features modern amenities while maintaining an authentic connection to nature. Wake up to the sounds of exotic birds and enjoy your morning coffee on the private balcony overlooking the canopy.`,
    amenities: [
      { icon: "Wifi", name: "Free Wi-Fi" },
      { icon: "Car", name: "Free Parking" },
      { icon: "Snowflake", name: "Air Conditioning" },
      { icon: "Tv", name: "Smart TV" },
      { icon: "Coffee", name: "Coffee Maker" },
      { icon: "Bath", name: "Private Bathroom" },
      { icon: "Utensils", name: "Mini Refrigerator" },
      { icon: "Shield", name: "24/7 Security" },
      { icon: "Shirt", name: "Laundry Service" },
      { icon: "Phone", name: "Room Service" },
      { icon: "TreePine", name: "Forest View" },
      { icon: "Wind", name: "Balcony/Terrace" }
    ],
    highlights: [
      "Panoramic forest views from private balcony",
      "Spacious living area with modern furnishings",
      "Premium bedding and luxury bathroom amenities",
      "Complimentary welcome drinks and local snacks"
    ],
    policies: [
      { title: "Check-in", value: "2:00 PM onwards" },
      { title: "Check-out", value: "11:00 AM" },
      { title: "Cancellation", value: "Free cancellation up to 24 hours before check-in" },
      { title: "Pets", value: "Pet-friendly (additional charges apply)" },
      { title: "Smoking", value: "Non-smoking property" },
      { title: "Age Restriction", value: "Minimum age 18 years" }
    ]
  };

  const roomData = accommodation || defaultAccommodation;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="border-b border-border pb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              {roomData?.name}
            </h1>
            <div className="flex items-center space-x-4 text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Users" size={16} />
                <span className="text-sm">Up to {roomData?.maxGuests} guests</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Bed" size={16} />
                <span className="text-sm">{roomData?.bedrooms} bedroom{roomData?.bedrooms > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Bath" size={16} />
                <span className="text-sm">{roomData?.bathrooms} bathroom{roomData?.bathrooms > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Home" size={16} />
                <span className="text-sm">{roomData?.area}</span>
              </div>
            </div>
          </div>
          <div className="bg-primary/10 px-3 py-1 rounded-full">
            <span className="text-primary font-medium text-sm">{roomData?.type}</span>
          </div>
        </div>
      </div>
      {/* Description */}
      <div>
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          About This Accommodation
        </h2>
        <div className="prose prose-gray max-w-none">
          {roomData?.description?.split('\n')?.map((paragraph, index) => (
            <p key={index} className="text-muted-foreground leading-relaxed mb-4 last:mb-0">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
      {/* Amenities */}
      <div>
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Amenities & Services
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {roomData?.amenities?.map((amenity, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
              <Icon name={amenity?.icon} size={20} className="text-primary" />
              <span className="text-sm font-medium text-foreground">{amenity?.name}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Highlights */}
      <div>
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Special Features
        </h2>
        <div className="space-y-3">
          {roomData?.highlights?.map((highlight, index) => (
            <div key={index} className="flex items-start space-x-3">
              <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
              <span className="text-muted-foreground">{highlight}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Policies */}
      <div>
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Policies & Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roomData?.policies?.map((policy, index) => (
            <div key={index} className="flex justify-between items-start p-4 bg-muted/30 rounded-lg">
              <span className="font-medium text-foreground">{policy?.title}:</span>
              <span className="text-muted-foreground text-right ml-4">{policy?.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AccommodationInfo;